
import React from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, FileText, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function TeacherDashboard() {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Check if user has teacher access
  const isTeacher = user?.role === 'admin' || (user?.role === 'user' && user?.user_type === 'teacher');

  if (!isTeacher) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const teacherName = user?.teacher_full_name || user?.full_name || user?.email;
  const teacherEmail = user?.email;

  const { data: myRosterEntries = [] } = useQuery({
    queryKey: ['myRosterEntries', teacherEmail],
    queryFn: async () => {
      if (!teacherEmail) return [];
      // Only show active entries (after closeout, completed/excused/asd entries are filtered out)
      const allEntries = await base44.entities.RosterEntry.filter({ teacher_email: teacherEmail });
      return allEntries.filter(e => e.status === 'active');
    },
    enabled: !!teacherEmail,
  });

  const { data: myExcusals = [] } = useQuery({
    queryKey: ['myExcusals', teacherEmail],
    queryFn: () => base44.entities.ExcusalSubmission.filter({ teacher_email: teacherEmail }),
    enabled: !!teacherEmail,
  });

  const activeAssignments = myRosterEntries.filter(e => e.status === 'active').length;
  const pendingExcusals = myExcusals.filter(e => e.status === 'pending').length;
  const excusedCount = myRosterEntries.filter(e => e.status === 'excused').length;

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-blue-100 text-blue-800",
      excused: "bg-green-100 text-green-800",
      completed: "bg-slate-100 text-slate-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const getAttendanceBadge = (status) => {
    const colors = {
      present: "bg-green-100 text-green-800",
      absent: "bg-yellow-100 text-yellow-800",
      refusal: "bg-red-100 text-red-800",
      pending: "bg-slate-100 text-slate-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Teacher Dashboard</h1>
          <p className="text-slate-600 mt-1">Welcome Back, {teacherName}</p>
        </div>

        {!teacherEmail && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Your account does not have an email address. Please contact an administrator.
            </AlertDescription>
          </Alert>
        )}

        {teacherEmail && myRosterEntries.length === 0 && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>No students assigned yet.</strong> Your roster will appear here when the administrator uploads a failure report with your email address ({teacherEmail}).
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-800 to-red-900 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">My Active Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{activeAssignments}</div>
                <ClipboardList className="w-12 h-12 opacity-80" />
              </div>
              <Link to={createPageUrl("TeacherRoster")}>
                <Button variant="link" className="text-white hover:text-white/80 p-0 mt-2 h-auto">
                  View Full Roster →
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Pending Excusals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{pendingExcusals}</div>
                <Clock className="w-12 h-12 opacity-80" />
              </div>
              <Link to={createPageUrl("TeacherExcusals")}>
                <Button variant="link" className="text-white hover:text-white/80 p-0 mt-2 h-auto">
                  Review Requests →
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Excused Students</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{excusedCount}</div>
                <CheckCircle className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">This period</p>
            </CardContent>
          </Card>
        </div>

        {myRosterEntries.length > 0 && (
          <Card className="border-none shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>My Recent Assignments</CardTitle>
                <Link to={createPageUrl("TeacherRoster")}>
                  <Button variant="outline" size="sm">
                    View All
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Attendance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myRosterEntries.slice(0, 10).map((entry) => (
                      <TableRow key={entry.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {entry.student_first_name} {entry.student_last_name}
                        </TableCell>
                        <TableCell>{entry.course_short_description || entry.course}</TableCell>
                        <TableCell>
                          <span className="font-semibold text-red-600">{entry.athletic_grade || entry.assignment_grade}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(entry.status)}>
                            {entry.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getAttendanceBadge(entry.attendance_status)}>
                            {entry.attendance_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to={createPageUrl("TeacherRoster")}>
              <Button className="w-full justify-start" variant="outline">
                <ClipboardList className="w-4 h-4 mr-2" />
                View My Roster
              </Button>
            </Link>
            <Link to={createPageUrl("TeacherExcusals")}>
              <Button className="w-full justify-start" variant="outline">
                <FileText className="w-4 h-4 mr-2" />
                Review Excusal Requests
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
